/**
 * Thin HTTP client for the Antioch workspace server's agent REST endpoints.
 *
 * The WebSocket (see ``transport.ts``) is the hot path — this module
 * covers the cold metadata endpoints the picker UI needs: browse the
 * filesystem, list and mutate sessions within a project, ingest
 * uploads. Every request goes to the ``/workspace/agent`` prefix so
 * vite's dev proxy (and prod's Envoy) route it alongside the
 * WebSocket.
 *
 * Routing + auth
 * --------------
 * Same routing contract as ``transport.ts``: in deployed mode the
 * webapp lives at the platform origin and the workspace server lives
 * behind Envoy at the per-workspace hostname, so we MUST issue
 * absolute URLs against the workspace's HTTPS origin (otherwise the
 * fetch hits the platform origin and 404s). Auth flows as a
 * ``?token=<workspace_token>`` query param so Envoy's JWT extractor
 * can validate the cross-origin call.
 *
 * The AgentPanelProvider calls :func:`configureAgentApi` whenever
 * the connected workspace or its service tokens change; every call
 * here reads the current config to build the URL. When unset (local
 * dev with the Vite proxy), we fall back to a relative ``/workspace/agent/*``
 * path against same-origin and skip the token (no Envoy in front of
 * a localhost workspace server).
 */

import type { OutboundFrame, TaskStartedFrame, ToolResultBlock } from './types';

const WORKSPACE_AGENT_PREFIX = '/workspace/agent';

interface AgentApiConfig {
  /**
   * Workspace HTTPS origin (``https://ws-XXXX.<env>.antioch.com``). When
   * present, every fetch goes through ``${baseUrl}/workspace/agent/...``. Unset
   * in local dev where the Vite proxy forwards same-origin
   * ``/workspace/*`` to the workspace server on ``localhost:8899``.
   */
  baseUrl: string | null;
  /**
   * Workspace JWT for Envoy. Appended as ``?token=`` on each request.
   * ``null`` in local dev (no Envoy gate to satisfy).
   */
  workspaceToken: string | null;
}

let _config: AgentApiConfig = { baseUrl: null, workspaceToken: null };

/**
 * Update the routing + auth context every fetch in this module uses.
 *
 * Called from ``AgentPanelProvider`` on workspace-connect and on every
 * ``serviceTokens`` refresh so the agent panel always points at the
 * currently-connected workspace's Envoy with a fresh, non-expired JWT.
 */
export function configureAgentApi(config: AgentApiConfig): void {
  _config = config;
}

/**
 * Build a full URL for a path under the workspace server agent API. Honors the configured
 * ``baseUrl`` when set; falls back to relative same-origin for local
 * dev. Appends the workspace token as ``?token=`` so cross-origin
 * fetches authenticate against Envoy.
 */
export function buildAgentUrl(path: string): string {
  const { baseUrl, workspaceToken } = _config;
  const fullPath = `${WORKSPACE_AGENT_PREFIX}${path}`;
  if (!baseUrl) {
    // Local dev / tests: relative same-origin URL. Vite proxy forwards
    // /workspace/* to localhost:8899; no Envoy means no token gate.
    return fullPath;
  }
  if (!workspaceToken) {
    throw new Error('Agent API workspace token is not ready');
  }
  const url = new URL(fullPath, baseUrl);
  url.searchParams.set('token', workspaceToken);
  return url.toString();
}

export interface AgentSessionEntry {
  session_id: string;
  first_prompt: string | null;
  summary: string | null;
  /** Epoch milliseconds — last write to the session JSONL. ``null`` when the SDK can't ``stat`` it. */
  last_modified: number | null;
  /**
   * Epoch milliseconds of the most recent *user-visible* turn in the JSONL —
   * a real ``user`` / non-synthetic ``assistant`` record. ``null`` when the
   * tail contains only metadata (custom-title, file-history-snapshot,
   * last-prompt) or synthetic frames Claude Code injected after upstream
   * API failures. Sidebar prefers this over ``last_modified`` so non-content
   * writes don't masquerade as new activity. Older workspace servers omit the
   * field; callers should fall back to ``last_modified``.
   */
  last_event_at?: number | null;
  /** Epoch milliseconds — session creation time. ``null`` when not recorded. */
  created_at: number | null;
  /**
   * ``true`` when a live WebSocket on the server is currently bridged to this
   * session id — drives the in-progress spinner badge in the picker.
   * Older workspace servers omit the field, so callers should default to ``false``.
   */
  is_active?: boolean;
}

export interface AgentSessionsPage {
  cwd: string;
  sessions: AgentSessionEntry[];
  limit?: number;
  offset?: number;
  next_offset?: number | null;
  has_more?: boolean;
}

export interface AgentDirectoryEntry {
  name: string;
  path: string;
  is_dir: boolean;
  /** Epoch seconds (float). ``null`` when the server couldn't ``stat`` the entry. */
  last_modified?: number | null;
  /**
   * Number of persisted Claude sessions for this folder
   * (``~/.claude/projects/<encoded-cwd>/*.jsonl``). The picker uses
   * this to badge folders with their history depth so users can spot
   * projects they've worked in before. Older workspace servers omit the
   * field; treat as ``0``.
   */
  session_count?: number;
}

export interface AgentDirectoryListing {
  /** Absolute path of the folder that was listed. */
  path: string;
  /** Parent folder (``null`` only at the filesystem root). */
  parent: string | null;
  /** Resolved ``$HOME`` so the picker can render a "Home" anchor. */
  home: string;
  /** Sub-folders of ``path``. Plain files are omitted server-side. */
  entries: AgentDirectoryEntry[];
  /**
   * Number of persisted Claude sessions for the listed path itself
   * (not its children). Lets the picker surface "this folder already
   * has N sessions" on the primary CTA when the user is standing
   * inside an existing project. Older workspace servers omit the field;
   * treat as ``0``.
   */
  session_count?: number;
}

/**
 * Perpetual session state derived from the full JSONL transcript on
 * every messages-page fetch. Lets the webapp hydrate activity
 * badges (currently the todos checklist) the instant the initial
 * history call returns, regardless of whether the loaded slice
 * happens to contain the originating ``TodoWrite`` tool call.
 */
export interface SessionStateSnapshot {
  /**
   * ``true`` when the server currently considers this session busy.
   * This is the same source as ``AgentSessionEntry.is_active`` but is
   * included with history so a reconnect can hydrate the chat tail
   * with the right in-flight affordances before the next live frame.
   */
  is_active?: boolean;
  /**
   * Canonical server-owned duration for the latest completed parent
   * user turn. ``null`` means the latest turn is incomplete or the
   * server cannot derive a completed duration.
   */
  last_completed_turn_duration_ms?: number | null;
  /**
   * Server epoch milliseconds for the currently active parent turn,
   * when the session is busy. Used to keep the footer timer stable
   * across reconnect/history hydration.
   */
  active_turn_started_at_ms?: number | null;
  /**
   * Server-derived current context high-water after the latest compact
   * boundary. This comes from the full transcript scan rather than the
   * paginated message slice so older scrollback cannot inflate the chip.
   */
  context_tokens_high_water?: number;
  /**
   * Latest ``TodoWrite`` tool_use across the whole transcript, or
   * ``null`` when the agent never wrote a checklist this session.
   */
  latest_todo_write: { tool_use_id: string; input: Record<string, unknown> } | null;
  /**
   * Latest task-list snapshot derived from ``TaskCreate`` /
   * ``TaskUpdate`` / ``TaskGet`` / ``TaskList`` tool results.
   * Newer Claude Code headless sessions use these instead of
   * ``TodoWrite``.
   */
  latest_task_state?: { tool_use_id: string; tasks: Record<string, unknown>[] } | null;
  /**
   * Legacy field older/newer workspace servers may include. The
   * current webapp ignores plan-mode proposals; plan mode is only
   * exposed from inside the workspace CLI.
   */
  latest_exit_plan_mode: { tool_use_id: string; input: Record<string, unknown> } | null;
  /**
   * Background tasks the server's full-transcript scan has classified
   * as still running (terminal ``<task-notification>`` has not yet
   * landed in the JSONL). Replaces ``activity.activeTasks`` on the
   * client wholesale — there is no client-side notification replay.
   */
  active_tasks?: TaskStartedFrame[];
  /**
   * Synthetic terminal tool results inferred from Claude Code's
   * persisted interrupt sentinel. These let refresh display an
   * interrupted tool as stopped instead of resurrecting it as pending.
   */
  interrupted_tool_results?: ToolResultBlock[];
}

export interface SessionMessagesPage {
  session_id: string;
  /** Total number of messages on disk for this session. */
  total: number;
  /** Inclusive lower bound (offset) of the returned slice in the full chronological list. */
  start_offset: number;
  /** Exclusive upper bound (offset) of the returned slice. */
  end_offset: number;
  /** ``true`` when older messages exist on disk (i.e. ``start_offset > 0``). */
  has_more: boolean;
  /**
   * Wire-shape-compatible historical frames: ``assistant`` and ``user``
   * frames identical to what the live WebSocket would emit. Feed them
   * through the same reducer path used for live frames.
   */
  messages: OutboundFrame[];
  /**
   * Perpetual checklist snapshot derived from the full transcript.
   * Older workspace servers omit the field; treat as
   * ``{ latest_todo_write: null, latest_exit_plan_mode: null }``.
   */
  session_state?: SessionStateSnapshot;
}

async function postJson<T>(path: string, body: unknown = {}): Promise<T> {
  const response = await fetch(buildAgentUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`agent ${path} → ${response.status}`);
  return (await response.json()) as T;
}

/**
 * List entries under ``path`` (defaults to ``$HOME``). Powers the
 * file-tree project picker and the agent input's ``@`` path menu.
 * Callers opt into regular files with ``includeFiles`` so the
 * project picker keeps its folder-only behavior.
 */
export async function fetchDirectoryEntries(
  path?: string,
  opts: { showHidden?: boolean; includeFiles?: boolean } = {}
): Promise<AgentDirectoryListing> {
  return postJson<AgentDirectoryListing>('/project/browse', {
    path: path ?? null,
    show_hidden: opts.showHidden ?? false,
    include_files: opts.includeFiles ?? false,
  });
}

/**
 * Create a new sub-folder under ``parent``. Returns the new entry in
 * the same shape as the rows from :func:`fetchDirectoryEntries`, so
 * the picker can navigate into it without re-listing.
 */
export async function createDirectory(parent: string, name: string): Promise<AgentDirectoryEntry> {
  return postJson<AgentDirectoryEntry>('/project/mkdir', { parent, name });
}

export async function fetchSessionsPage(
  cwd: string,
  opts: { limit?: number; offset?: number } = {}
): Promise<AgentSessionsPage> {
  const limit = opts.limit ?? 50;
  const offset = opts.offset ?? 0;
  return postJson<AgentSessionsPage>('/project/sessions', { cwd, limit, offset });
}

export async function fetchSessions(
  cwd: string,
  opts: { limit?: number; offset?: number } = {}
): Promise<AgentSessionEntry[]> {
  const { sessions } = await fetchSessionsPage(cwd, opts);
  return sessions;
}

export async function fetchSessionInfo(cwd: string, sessionId: string): Promise<AgentSessionEntry | null> {
  try {
    const { session } = await postJson<{ cwd: string; session: AgentSessionEntry | null }>('/project/sessions/info', {
      cwd,
      session_id: sessionId,
    });
    return session;
  } catch {
    return null;
  }
}

/** Rename a session — pure pass-through of ``claude_agent_sdk.rename_session``. */
export async function renameSession(cwd: string, sessionId: string, title: string): Promise<void> {
  await postJson('/project/sessions/rename', { cwd, session_id: sessionId, title });
}

export interface DeleteSessionResponse {
  ok: boolean;
  session_id: string;
  /**
   * ``true`` when the server's hard-delete was a no-op because the
   * JSONL had already been removed (raced with a prior delete,
   * manually unlinked, never persisted). Callers should still treat
   * this as success and refresh their listing -- the disk state is
   * what the user asked for either way.
   */
  already_deleted: boolean;
}

/**
 * Hard-delete a session. Idempotent on the server side: a missing
 * JSONL surfaces as ``already_deleted: true`` rather than a 500, so
 * the UI doesn't get stuck looping on a stale row.
 */
export async function deleteSession(cwd: string, sessionId: string): Promise<DeleteSessionResponse> {
  return postJson<DeleteSessionResponse>('/project/sessions/delete', { cwd, session_id: sessionId });
}

/**
 * Fetch a window of historical messages for a persisted session.
 *
 * Pagination model mirrors the server: pass ``before`` set to the
 * previous response's ``start_offset`` to load the prior page; omit
 * ``before`` to load the most recent ``limit`` messages.
 */
export async function fetchSessionMessages(
  cwd: string,
  sessionId: string,
  opts: { limit?: number; before?: number; includeState?: boolean } = {}
): Promise<SessionMessagesPage> {
  return postJson<SessionMessagesPage>('/project/sessions/messages', {
    cwd,
    session_id: sessionId,
    limit: opts.limit ?? 50,
    before: opts.before ?? null,
    include_state: opts.includeState ?? true,
  });
}

/**
 * One slice of a background task's captured stdout/stderr.
 *
 * The workspace server reads ``/tmp/claude-<UID>/<encoded-cwd>/<sid>/tasks/<task-id>.output``
 * — Claude Code's per-task buffer — and returns the bytes between
 * ``offset`` (inclusive) and ``end_offset`` (exclusive). Pass the
 * previous response's ``end_offset`` as the next call's offset to
 * tail incrementally without re-fetching what we already have.
 *
 * ``exists: false`` is the normal "task hasn't produced any output
 * yet" case — the webapp shows an empty viewer rather than an error.
 */
export interface TaskOutputSlice {
  exists: boolean;
  content: string;
  offset: number;
  end_offset: number;
  file_size: number;
  eof: boolean;
}

export async function fetchTaskOutput(
  cwd: string,
  sessionId: string,
  taskId: string,
  opts: { offset?: number; maxBytes?: number; tail?: boolean } = {}
): Promise<TaskOutputSlice> {
  const offset = opts.offset ?? 0;
  const path = '/project/sessions/tasks/output';
  const res = await fetch(buildAgentUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      cwd,
      session_id: sessionId,
      task_id: taskId,
      offset,
      max_bytes: opts.maxBytes ?? 131072,
      tail: opts.tail ?? false,
    }),
  });
  // The on-disk path Claude Code writes to is undocumented and
  // sometimes missing entirely (the CLI hasn't created the buffer
  // yet). The CLI's "no output" case looks like a 404 to us, so
  // degrade to a calm empty slice instead of surfacing an error.
  if (res.status === 404) {
    return { exists: false, content: '', offset, end_offset: offset, file_size: 0, eof: true };
  }
  if (!res.ok) throw new Error(`agent /project/sessions/tasks/output → ${res.status}`);
  return (await res.json()) as TaskOutputSlice;
}

export interface UploadedAttachment {
  /** Absolute path on the workspace filesystem — what the agent gets handed in the next user message. */
  path: string;
  /** Sanitized filename (no path components, no unsafe chars) the server actually wrote to disk. */
  name: string;
  /** Bytes written. */
  size: number;
  /** Best-effort MIME type the browser reported. */
  mime: string;
}

export interface UploadOptions {
  /**
   * Progress callback. Fires repeatedly during the upload with a
   * value in ``[0, 1]``. Note: the browser only knows about the
   * upload-byte phase, so 100% means "all bytes shipped to the
   * server" — there's still a tiny tail until the server's JSON
   * response lands. We treat that gap as 100% in the UI.
   */
  onProgress?: (fraction: number) => void;

  /**
   * AbortSignal — when fired, cancels the in-flight upload. The
   * promise rejects with an ``Error`` whose ``name`` is
   * ``"AbortError"``.
   */
  signal?: AbortSignal;
}

/**
 * Upload a file to the workspace server's process-wide temp uploads
 * directory, returning the resolved absolute path the agent can
 * ``Read`` / ``Bash``.
 *
 * Files land under ``$TMPDIR/antioch-workspace-uploads/`` rather than the
 * user's project tree — drag-and-drop noise stays out of the
 * workspace. The agent decides whether to keep the file in place or
 * copy it into the project deliberately.
 *
 * Uses ``XMLHttpRequest`` rather than ``fetch`` because the browser's
 * fetch API still doesn't expose per-chunk upload progress events;
 * the chip's progress bar would otherwise jump from 0 to 100 with no
 * intermediate state for big CAD files.
 */
export function uploadFile(file: File, opts: UploadOptions = {}): Promise<UploadedAttachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildAgentUrl('/project/upload'));
    xhr.responseType = 'json';

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return;
      opts.onProgress?.(Math.min(1, event.loaded / event.total));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1);
        resolve(xhr.response as UploadedAttachment);
        return;
      }
      const detail =
        xhr.response && typeof xhr.response === 'object' && 'detail' in xhr.response
          ? String((xhr.response as { detail: unknown }).detail)
          : `upload failed with status ${xhr.status}`;
      reject(new Error(detail));
    });

    xhr.addEventListener('error', () => reject(new Error('upload network error')));
    xhr.addEventListener('abort', () => {
      const err = new Error('upload aborted');
      err.name = 'AbortError';
      reject(err);
    });

    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        return;
      }
      opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    const body = new FormData();
    body.append('file', file);
    xhr.send(body);
  });
}
